/**
 * LoopsPanel — F.4 template-based loop diagram generator per
 * `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.2.
 *
 * Lists each instrument loop (tag → I/O type → terminal). A one-click
 * "Generate from PDO" button auto-seeds the list from `pdo.instrumentation`.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, Wand2, ListChecks } from "lucide-react";
import api, { formatErr } from "../lib/api";

const IO_TYPES = ["AI (4-20mA)", "AI (HART)", "AO", "DI", "DO",
                   "Fieldbus H1", "Profibus PA", "Wireless"];

export default function LoopsPanel({ projectId }) {
  const [busy, setBusy] = useState(false);
  const [loops, setLoops] = useState([]);
  const [toast, setToast] = useState(null);

  const show = (t, kind = "ok") => {
    setToast({ t, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/projects/${projectId}/loops`);
      setLoops(r.data?.loops || []);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${projectId}/loops`, { loops });
      show(`Saved ${r.data.count} loops`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/loops/generate`);
      setLoops(r.data?.loops || []);
      show(`Generated ${r.data.count} loops from PDO instruments`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const add = () => setLoops([...loops, {
    loop_id: `L-${(loops.length + 101)}`, tag: "", type: "AI (4-20mA)",
    process_fn: "", set_point: "", interlock: "", io_card: "", terminal: "",
  }]);
  const upd = (i, p) => setLoops(loops.map((l, j) => i === j ? { ...l, ...p } : l));
  const del = (i) => setLoops(loops.filter((_, j) => j !== i));

  return (
    <div data-testid="loops-panel"
         style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <ListChecks size={14} color="var(--pf-accent)" />
        <span className="pf-overline" style={{ margin: 0 }}>LOOP DIAGRAMS (F.4)</span>
        <span style={{ fontSize: 11, color: "var(--pf-text-dim)",
                        fontFamily: "'JetBrains Mono', monospace" }}>
          · {loops.length} loops
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" data-testid="loops-generate-btn"
                 onClick={generate} disabled={busy}
                 style={{ ...btn, borderStyle: "dashed" }}>
          <Wand2 size={12}/> Generate from PDO
        </button>
        <button className="btn btn-ghost" data-testid="loops-add-btn"
                 onClick={add}
                 style={btn}>
          <Plus size={12}/> Add
        </button>
        <button className="btn btn-primary" data-testid="loops-save-btn"
                 onClick={save} disabled={busy} style={btn}>
          {busy ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save
        </button>
      </div>

      <div style={{ overflow: "auto", border: "1px solid var(--pf-border)",
                     borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse",
                          fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
          <thead style={{ background: "var(--pf-surface-2)" }}>
            <tr>
              {["Loop ID", "Tag", "I/O Type", "Function", "Set Point",
                "Interlock", "I/O Card", "Terminal", ""].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loops.map((l, i) => (
              <tr key={i} data-testid={`loop-row-${i}`}>
                <td style={td}><input style={cellInp}
                         data-testid={`loop-${i}-id`}
                         value={l.loop_id}
                         onChange={(e) => upd(i, { loop_id: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         data-testid={`loop-${i}-tag`}
                         value={l.tag}
                         onChange={(e) => upd(i, { tag: e.target.value })}/></td>
                <td style={td}>
                  <select style={tdInp} value={l.type}
                           data-testid={`loop-${i}-type`}
                           onChange={(e) => upd(i, { type: e.target.value })}>
                    {IO_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </td>
                <td style={td}><input style={cellInp}
                         value={l.process_fn || ""}
                         onChange={(e) => upd(i, { process_fn: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         value={l.set_point || ""}
                         onChange={(e) => upd(i, { set_point: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         value={l.interlock || ""}
                         onChange={(e) => upd(i, { interlock: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         value={l.io_card || ""}
                         onChange={(e) => upd(i, { io_card: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         value={l.terminal || ""}
                         onChange={(e) => upd(i, { terminal: e.target.value })}/></td>
                <td style={td}>
                  <button className="btn btn-ghost"
                           data-testid={`loop-${i}-del`}
                           onClick={() => del(i)}
                           style={{ ...btn, color: "#ef4444", padding: "2px 4px" }}>
                    <Trash2 size={10}/>
                  </button>
                </td>
              </tr>
            ))}
            {!loops.length && (
              <tr><td colSpan={9} style={{ ...td, textAlign: "center",
                                               color: "var(--pf-text-dim)" }}>
                No loops — click "Generate from PDO" to auto-seed from PDO instruments.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {toast && (
        <div data-testid={`loops-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 28, right: 28,
          background: toast.kind === "ok" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          padding: "8px 14px", fontSize: 12, borderRadius: 3, zIndex: 10001,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{toast.t}</div>
      )}
    </div>
  );
}

const btn = { fontSize: 11, padding: "4px 10px", borderRadius: 3 };
const inp = { background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
               color: "var(--pf-fg)", padding: "4px 8px", fontSize: 11,
               borderRadius: 3, fontFamily: "'JetBrains Mono', monospace" };
const th = { padding: "6px 8px", textAlign: "left", color: "var(--pf-text-dim)",
              borderBottom: "1px solid var(--pf-border)", fontSize: 10,
              whiteSpace: "nowrap", textTransform: "uppercase" };
const td = { padding: 4, borderBottom: "1px solid var(--pf-border-dim, #222)",
              verticalAlign: "top" };
const tdInp = { ...inp, width: "100%", fontSize: 10, padding: "2px 4px" };
const cellInp = { ...inp, width: "100%", minWidth: 90, fontSize: 10, padding: "2px 4px" };
